import {
  ArrowRight,
  Box,
  Check,
  Copy,
  Database,
  Download,
  Layers,
  type LucideIcon,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Schema } from '@/services/api/schemas'
import { cn } from '@/utils/cn'
import { type SchemaHeaderAction, SchemaHeaderActions } from './SchemaHeaderActions'

/**
 * Promoted schema identity — the schema name as a real page heading instead of
 * being buried in a card. Shared by every layout prototype so the variants only
 * differ in how the schema is *selected*, not how it is *presented*.
 */
export function SchemaHero({
  schema,
  actions,
  leading,
  below,
  showStats = true,
  showDescription = true,
}: {
  schema: Schema
  /** The heading row's action cluster. Declared as data so the header can render
   * it as buttons or, when it stops fitting, as one overflow menu. */
  actions?: SchemaHeaderAction[]
  /** Replaces the default icon tile (e.g. a back button in the gallery editor). */
  leading?: ReactNode
  /** Extra content under the meta chips (e.g. a version switcher row). */
  below?: ReactNode
  /** Show the entity/relation/pattern count chips. Off in the editor, where the
   * tab switcher already carries those counts (avoids a duplicated triad). */
  showStats?: boolean
  /** Render the schema description. Off when the caller renders its own (e.g. the
   * case editor pairs it with an inline edit control). */
  showDescription?: boolean
}) {
  const { rowRef, actionsRef, collapsed } = useActionsCollapse(schema.id, actions)
  return (
    <div className="flex flex-wrap items-start gap-x-4 md:flex-nowrap">
      {leading ?? <IconTile icon={Database} />}
      {/* The name and its toolbar hold one line — the cluster collapses to an
       * overflow menu rather than wrapping — and the description, stats and
       * `below` slot run the full width underneath.
       *
       * Below md this wrapper dissolves (`contents`), so the title row sits
       * beside the leading tile and the stack below starts at the page edge
       * instead of inheriting the tile's indent; the name may take two lines
       * and the version pill drops under it rather than crushing the name. */}
      <div className="max-md:contents min-w-0 flex-1">
        <div
          ref={rowRef}
          className="flex min-w-0 flex-1 items-start justify-between gap-x-2 md:items-center md:gap-x-4"
        >
          <div className="flex max-md:flex-wrap min-w-0 items-center gap-2.5 max-md:gap-y-1">
            <h1 className="max-md:line-clamp-2 max-md:break-words md:truncate text-xl md:text-2xl font-semibold tracking-tight text-foreground">
              {schema.name}
            </h1>
            <VersionPill schema={schema} />
          </div>
          {actions && actions.length > 0 && (
            <div ref={actionsRef} className="flex shrink-0 items-center gap-2">
              <SchemaHeaderActions actions={actions} collapsed={collapsed} />
            </div>
          )}
        </div>
        {/* One rhythm for everything under the title row, so neither this
         * component nor a `below` caller has to restate the spacing. The stack
         * hides itself when every slot is empty. */}
        <div className="mt-3 min-w-0 basis-full space-y-3 empty:hidden">
          {showDescription && schema.description && (
            <p className="max-w-3xl text-sm text-muted-foreground line-clamp-2">
              {schema.description}
            </p>
          )}
          {showStats && <SchemaMetaChips schema={schema} />}
          {below}
        </div>
      </div>
    </div>
  )
}

/** Width the schema name keeps for itself before the toolbar gives way. */
const MIN_TITLE_WIDTH = 220

/**
 * Decides whether the heading's action cluster still fits beside the name.
 *
 * The switch point follows the toolbar's own measured width rather than a
 * guessed breakpoint, so it holds for long schema names and for the languages
 * whose button labels run longer than English. The expanded cluster is the only
 * one worth measuring — once collapsed, the last measurement stands, which is
 * what keeps the two states from oscillating.
 */
function useActionsCollapse(schemaId: string, actions?: SchemaHeaderAction[]) {
  const { i18n } = useTranslation()
  const rowRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const expandedWidth = useRef(0)
  const [collapsed, setCollapsed] = useState(false)

  const measure = useCallback(() => {
    const row = rowRef.current
    if (!row) return
    // Both DOM reads happen here rather than inside the updater, which React may
    // run more than once.
    const rowWidth = row.clientWidth
    const clusterWidth = actionsRef.current?.scrollWidth ?? 0
    setCollapsed(wasCollapsed => {
      if (!wasCollapsed && clusterWidth) expandedWidth.current = clusterWidth
      if (!expandedWidth.current) return wasCollapsed
      return expandedWidth.current + MIN_TITLE_WIDTH > rowWidth
    })
  }, [])

  // The observer covers every change to the row's geometry. These deps cover the
  // rest — what the cluster itself is made of, and the language its labels are
  // in — so the measurement never has to run on unrelated parent re-renders,
  // where reading `scrollWidth` would force a layout flush per keystroke.
  const clusterKey = actions?.map(action => `${action.id}:${action.label}`).join('|')
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberate re-measure triggers
  useLayoutEffect(measure, [measure, clusterKey, schemaId, i18n.language])
  useEffect(() => {
    const row = rowRef.current
    if (!row) return
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    return () => observer.disconnect()
  }, [measure])

  return { rowRef, actionsRef, collapsed }
}

/** The standard primary-tinted icon tile used as a header's leading badge. */
export function IconTile({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <div className="h-11 w-11 rounded-xl border border-primary/40 bg-primary/5 flex items-center justify-center shrink-0">
      <Icon className="h-5.5 w-5.5 text-primary" />
    </div>
  )
}

/** Version pill (`vN` + active dot) shown next to a schema name. */
export function VersionPill({ schema }: { schema: Schema }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-card/60 px-2 py-0.5 text-xs text-muted-foreground tabular-nums">
      v{schema.version}
      {schema.is_active && (
        <>
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          <span className="text-success-foreground">Active</span>
        </>
      )}
    </span>
  )
}

/** Compact stat row: entity / relation / pattern counts. */
export function SchemaMetaChips({ schema, className }: { schema: Schema; className?: string }) {
  // List rows carry *_count; detail rows carry the full arrays. Support both so
  // this renders in the gallery (list) and the editor hero (detail) alike.
  const entityCount = schema.entity_count ?? schema.entity_types?.length ?? 0
  const relationCount = schema.relation_count ?? schema.relationship_types?.length ?? 0
  const patternCount = schema.pattern_count ?? schema.patterns?.length ?? 0
  return (
    <div className={cn('flex items-center gap-2 flex-wrap', className)}>
      <Stat icon={Box} tone="text-info-foreground" value={entityCount} label="Entities" />
      <Stat
        icon={ArrowRight}
        tone="text-warning-foreground"
        value={relationCount}
        label="Relations"
      />
      <Stat icon={Layers} tone="text-cat-3" value={patternCount} label="Patterns" />
    </div>
  )
}

function Stat({
  icon: Icon,
  tone,
  value,
  label,
}: {
  icon: LucideIcon
  tone: string
  value: number
  label: string
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/40 px-2 py-1 text-xs text-muted-foreground">
      <Icon className={cn('h-3.5 w-3.5', tone)} />
      <span className="font-medium text-foreground tabular-nums">{value}</span>
      {label}
    </span>
  )
}

/** Standard schema action cluster — Export / Import / version menu. Prototype
 * fixture: the controls are inert, only the shape matters. */
export function schemaToolbarActions(): SchemaHeaderAction[] {
  return [
    { id: 'export', label: 'Export', icon: Download },
    { id: 'import', label: 'Import JSON', icon: Upload },
    { id: 'new-version', label: 'New version', icon: Plus, inMenu: true },
    { id: 'duplicate', label: 'Duplicate schema', icon: Copy, inMenu: true },
    { id: 'set-active', label: 'Set as active', icon: Check, inMenu: true },
    { id: 'delete', label: 'Delete version', icon: Trash2, inMenu: true, destructive: true },
  ]
}
